import { VisitorGroup } from 'graphql/generated/graphqlTypes';
import { Translation } from 'hooks/useTranslation/useTranslation';
import { useMemo } from 'react';
import {
  getAccessStatusForVisitorGroup,
  getAllLockGroupsAssignedToVisitorGroup,
  getAllLocksAssignedDirectlyToVisitorGroup,
  getAmountOfMembersInGroup,
  isScheduleSetUpForVisitorGroup
} from 'utils/VisitorGroup/VisitorGroup';
import { getVisitorGroupOriginLabel } from 'v2/utils/visitorGroupOrigin';
import { useSearch } from './useSearch';

const visitorGroupColumns = ['name', 'searchableOrigin', 'hasNoUsers', 'hasNoSchedule', 'hasNoDevices', 'accessStatus'];

export function useVisitorGroupSearch({
  visitorGroups,
  search,
  translation
}: {
  visitorGroups: VisitorGroup[];
  search: string;
  translation: Translation;
}): VisitorGroup[] {
  const searchableVisitorGroups = useMemo(
    () =>
      visitorGroups.map((group: VisitorGroup) => {
        const assignedDevices = getAllLocksAssignedDirectlyToVisitorGroup(group);
        const assignedLockGroups = getAllLockGroupsAssignedToVisitorGroup(group);
        const hasNoDevices = assignedDevices.length === 0 && assignedLockGroups.length === 0;
        const isAccessComplete = getAccessStatusForVisitorGroup(group);

        return {
          ...group,
          searchableOrigin: getVisitorGroupOriginLabel(group, translation),
          hasNoUsers: getAmountOfMembersInGroup(group) > 0 ? '' : translation.no_users,
          hasNoSchedule: isScheduleSetUpForVisitorGroup(group) ? '' : translation.no_schedule,
          hasNoDevices: hasNoDevices ? translation.no_devices_assigned : '',
          accessStatus: isAccessComplete ? translation.complete : translation.incomplete
        };
      }),
    [visitorGroups, translation]
  );

  const searchedVisitorGroups = useSearch({
    data: searchableVisitorGroups,
    search,
    keys: visitorGroupColumns
  });

  return useMemo(
    () =>
      searchedVisitorGroups.map(
        ({ searchableOrigin, hasNoUsers, hasNoSchedule, hasNoDevices, accessStatus, ...group }) => group
      ),
    [searchedVisitorGroups]
  );
}
